'use client';

import { useMemo } from 'react';

import NumberFlow from '@number-flow/react';
import { debounce, useBoolean } from 'codekit';
import { motion } from 'framer-motion';
import { Waypoint } from 'react-waypoint';

import Pulse from 'components/Pulse';
import { AVERAGE_TIME_PER_COMMIT } from 'constants/institutional';
import { TRANSITION_DURATION } from 'constants/motion';
import { Metrics } from 'models/Metrics';
import Section from '../../';

import styles from './styles.module.css';

interface MetricsPureProps {
	metrics?: Metrics | null;
}

function MetricsPure({ metrics }: MetricsPureProps) {
	// Boolean hooks
	const isVisible = useBoolean(false);

	// Memo vars
	const values = useMemo(() => {
		const commits = metrics?.commits ? metrics.commits + 10195 : 0;
		const users = metrics?.users ? metrics.users + 154 : 0;
		const repositories = metrics?.repositories
			? metrics.repositories + 311
			: 0;

		return [
			{
				value: commits,
				description: 'Commits gerados',
			},
			{
				value: users,
				description: 'Usuários ativos',
			},
			{
				value: repositories,
				description: 'Repositórios otimizados',
			},
			{
				value: (commits * AVERAGE_TIME_PER_COMMIT) / 60 / 60,
				description: 'Tempo salvo (horas)',
			},
		];
	}, [metrics]);

	return (
		<Section
			className={styles.container}
			containerClassName={styles.containerContainer}
		>
			<motion.div
				className={styles.metrics}
				initial={{
					opacity: 0,
				}}
				animate={{
					opacity: metrics && isVisible.value ? 1 : 0,
				}}
				transition={{
					duration: TRANSITION_DURATION,
				}}
			>
				{values.map((metric) => (
					<Waypoint
						key={metric.description}
						onEnter={debounce(isVisible.setTrue, 1000)}
					>
						<div className={styles.metric}>
							<strong>
								<NumberFlow
									prefix="+"
									value={isVisible.value ? metric.value : 0}
								/>
							</strong>

							<span>{metric.description}</span>
						</div>
					</Waypoint>
				))}
			</motion.div>

			<motion.div
				className={styles.realtime__indicator}
				initial={{
					opacity: 0,
					filter: 'blur(10px)',
				}}
				animate={{
					opacity: isVisible.value ? 1 : 0,
					filter: 'blur(0px)',
				}}
				transition={{
					duration: TRANSITION_DURATION,
					delay: TRANSITION_DURATION,
				}}
			>
				<Pulse size={8} />
				<span>Atualizado em tempo real</span>
			</motion.div>
		</Section>
	);
}

export default MetricsPure;
